import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import { styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import parse from 'html-react-parser';
import React, { useCallback, useState } from 'react';
import NFTCard from '@/Component/NFTCard';
import NftCardSkeleton from '@/Component/NftCardSkeleton';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { useNftContent } from '@/lib/providers/NftContextProvider';
import CustomRadioGroup from './CustomRadioGroup';
import RenderIf from './RenderIf';
import { useNextPageObserver } from '@/lib/hooks/useNextPageObserver';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getAllTokens } from '@/services/tokens.service';
import { useCurrencyRate } from '@/lib/hooks/useCurrencyRate';

type Props = {
  title: string;
};

const StyledSearch = styled(TextField)(() => ({
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#A1A5AB'
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent'
    // border: '1px solid #A1A5AB'
  },
  '& .MuiInputBase-input': {
    color: '#fff'
  },
  '&::placeholder': {
    color: '#A1A5AB'
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#A1A5AB'
  },
  '& .MuiOutlinedInput-root:hover': {
    border: 'none'
  },
  '& .MuiOutlinedInput-root.Mui-focused': {
    '& > fieldset': {
      border: '1px solid #A1A5AB'
    },
    '&:hover': {
      backgroundColor: 'transparent'
    }
  }
}));

const NFTListing: React.FC<Props> = ({ title }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { priceData } = useCurrencyRate();
  const { isCountLoading, isNftListLoading, soldItemsIsLoading, nftFilter, tokenIds, soldItems } = useNftContent();

  const [searchValue, setSearchValue] = useState<string>('');

  const debouncedSearchValue = useDebounce<string>(searchValue, 300);

  const {
    data: masterData,
    isFetchingNextPage: isMasterDataFetching,
    isLoading: isMasterDataLoading,
    fetchNextPage: fetchNextMasterData,
    hasNextPage: hasNextMasterData
  } = useInfiniteQuery({
    queryKey: [
      'master_data',
      { filter: nftFilter.filter, search: debouncedSearchValue, soldItems: soldItems || [], tokenIds: tokenIds || [] }
    ],
    queryFn: async ({ pageParam = 0 }) =>
      await getAllTokens({
        offset: pageParam * 12,
        filter: nftFilter.filter,
        search: debouncedSearchValue,
        tokenIds: tokenIds || [],
        soldItems: soldItems || []
      }),
    getNextPageParam: (lastPage, allPages) => {
      const { hasNext } = lastPage.pagination;
      return hasNext ? allPages.length : undefined;
    },

    getPreviousPageParam: (_, allPages) => {
      return allPages.length > 1 ? allPages.length - 2 : undefined;
    },

    select: (data) => ({
      data: data.pages.flatMap((page) => page.data) || [],
      pagination: data?.pages[data?.pages.length - 1]?.pagination
    }),
    initialPageParam: 0,
    gcTime: 10 * 60 * 1000, // 10 minutes cache time
    staleTime: 5 * 60 * 1000, // 5 minutes stale time
    enabled: !!tokenIds && !isCountLoading && !soldItemsIsLoading
  });

  const isNoDataFound =
    !isNftListLoading &&
    !soldItemsIsLoading &&
    !isCountLoading &&
    !isMasterDataLoading &&
    !isMasterDataFetching &&
    masterData?.data?.length === 0;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value);
    },
    [setSearchValue]
  );

  const { sentinelRef } = useNextPageObserver({
    isLoading: isMasterDataFetching || isMasterDataLoading,
    hasNextPage: hasNextMasterData,
    onFetchNextPage: fetchNextMasterData
  });

  return (
    <Box mt="5rem" mb={{ xs: '74px', sm: 4 }}>
      <Box
        sx={{
          padding: {
            lg: '0px 120px 120px 120px',
            md: '0px 120px 120px 120px',
            xs: '16px 0 0 0'
          }
        }}
      >
        <Container maxWidth="lg">
          <Typography
            mb={4}
            sx={{
              fontFamily: theme?.MojitoClaim?.font?.primary,
              fontSize: '32px',
              lineHeight: isMobile ? '38px' : '120%',
              fontWeight: 500,
              textAlign: 'center',
              fontStyle: 'normal',
              color: theme?.palette?.text?.primary
            }}
          >
            {parse(title)}
          </Typography>
          <Grid
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            marginBottom={3}
            item
            xs={12}
            lg={12}
            md={12}
            sm={12}
            gap={2}
          >
            <StyledSearch
              required
              placeholder="Search by name, description or token ID"
              inputProps={{
                maxLength: 300
              }}
              autoComplete="off"
              value={searchValue}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: theme?.ClaimTemplate?.placeholderColor }} />
                  </InputAdornment>
                ),
                style: {
                  maxHeight: '40px',
                  fontWeight: 500,
                  fontSize: '16px',
                  lineHeight: '120%',
                  fontFamily: theme?.MojitoClaim?.font?.secondary,
                  color: theme?.ClaimTemplate?.placeholderColor
                }
              }}
              fullWidth
            />
            <RenderIf condition={!isMobile}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <CustomRadioGroup />
              </Grid>
            </RenderIf>
          </Grid>

          <RenderIf condition={isMobile}>
            <Box>
              <CustomRadioGroup />
            </Box>
          </RenderIf>

          <Grid container spacing={3} mt={1}>
            <RenderIf condition={!isNftListLoading && !soldItemsIsLoading && masterData?.data?.length !== 0}>
              {masterData?.data?.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item?.token_id + index}>
                  <NFTCard data={item} conversionRate={priceData} isSoldOut={item?.isSoldOut} />
                </Grid>
              ))}
            </RenderIf>
            <RenderIf
              condition={
                isNftListLoading || soldItemsIsLoading || isCountLoading || isMasterDataFetching || isMasterDataLoading
              }
            >
              {Array.from({ length: 4 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <NftCardSkeleton />
                </Grid>
              ))}
            </RenderIf>
          </Grid>

          <div ref={sentinelRef} style={{ height: '2px' }} />

          <RenderIf condition={isNoDataFound}>
            <Stack alignItems="center" justifyContent="center" position="relative" mt="40px">
              <svg width={50} height={50} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                <g id="SVGRepo_iconCarrier">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.29289 1.29289C9.48043 1.10536 9.73478 1 10 1H18C19.6569 1 21 2.34315 21 4V8C21 8.55228 20.5523 9 20 9C19.4477 9 19 8.55228 19 8V4C19 3.44772 18.5523 3 18 3H11V8C11 8.55228 10.5523 9 10 9H5V20C5 20.5523 5.44772 21 6 21H10C10.5523 21 11 21.4477 11 22C11 22.5523 10.5523 23 10 23H6C4.34315 23 3 21.6569 3 20V8C3 7.73478 3.10536 7.48043 3.29289 7.29289L9.29289 1.29289ZM6.41421 7H9V4.41421L6.41421 7ZM20.1716 18.7574C20.6951 17.967 21 17.0191 21 16C21 13.2386 18.7614 11 16 11C13.2386 11 11 13.2386 11 16C11 18.7614 13.2386 21 16 21C17.0191 21 17.967 20.6951 18.7574 20.1716L21.2929 22.7071C21.6834 23.0976 22.3166 23.0976 22.7071 22.7071C23.0976 22.3166 23.0976 21.6834 22.7071 21.2929L20.1716 18.7574ZM13 16C13 14.3431 14.3431 13 16 13C17.6569 13 19 14.3431 19 16C19 17.6569 17.6569 19 16 19C14.3431 19 13 17.6569 13 16Z"
                    fill="#ffff"
                  ></path>
                </g>
              </svg>
              <Typography
                style={{
                  color: theme.palette.text.primary,
                  fontFamily: theme?.MojitoClaim?.font.secondary,
                  fontWeight: 600,
                  paddingTop: '12px',
                  fontSize: '1rem',
                  lineHeight: '1.5'
                }}
              >
                No Data Found
              </Typography>
            </Stack>
          </RenderIf>
        </Container>
      </Box>
    </Box>
  );
};

export default NFTListing;
